import {
  Button,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Textarea,
} from '@/components/ui'
import { Check, Info } from 'lucide-react'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { SettingsLoadError } from '@/components/SettingsLoadError'
import { useState } from 'react'

export interface TitanEndpointSectionProps {
  title: string
  description: string
  endpoint: string | null
  caCertificate: string | null
  isLoading: boolean
  loadError?: unknown
  onRetryLoad?: () => void
  isSaving: boolean
  isDeleting: boolean
  onSave: (endpoint: string, caCertificate: string | null) => Promise<void>
  onDelete: () => Promise<void>
  testIdPrefix?: string
}

function isAbsoluteHttpUrl(value: string) {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function hasEmbeddedCredentials(value: string) {
  try {
    const url = new URL(value)
    return !!url.username || !!url.password
  } catch {
    return false
  }
}

const MAX_CA_LENGTH = 8192
const PEM_HEADER = '-----BEGIN CERTIFICATE-----'
const PEM_FOOTER = '-----END CERTIFICATE-----'

function isPemCertificate(value: string) {
  return value.includes(PEM_HEADER) && value.includes(PEM_FOOTER)
}

export function TitanEndpointSection({
  title,
  description,
  endpoint,
  caCertificate,
  isLoading,
  loadError,
  onRetryLoad,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
  testIdPrefix = 'tenant-titan',
}: TitanEndpointSectionProps) {
  const [endpointValue, setEndpointValue] = useState(endpoint ?? '')
  const [caValue, setCaValue] = useState(caCertificate ?? '')
  const [caTouched, setCaTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const endpointInputId = `${testIdPrefix}-endpoint-input`
  const caInputId = `${testIdPrefix}-ca-input`
  const isConfigured = !!endpoint

  const handleSave = async () => {
    setError(null)

    const trimmedEndpoint = endpointValue.trim()

    if (!trimmedEndpoint) {
      setError('Endpoint is required.')
      return
    }

    if (!isAbsoluteHttpUrl(trimmedEndpoint)) {
      setError('Endpoint must be an absolute http or https URL.')
      return
    }

    if (hasEmbeddedCredentials(trimmedEndpoint)) {
      setError("Endpoint must not embed a username or password. Each user connects their own key on the project's Connectors page.")
      return
    }

    const caToSend = caTouched ? caValue.trim() : null

    if (caToSend !== null) {
      if (caToSend.toUpperCase().includes('PRIVATE KEY')) {
        setError('Certificate authority must contain only certificates. Remove the private key.')
        return
      }

      if (caToSend && !isPemCertificate(caToSend)) {
        setError('Certificate authority must be in PEM format, containing -----BEGIN CERTIFICATE----- and -----END CERTIFICATE-----.')
        return
      }

      if (caToSend.length > MAX_CA_LENGTH) {
        setError(`Certificate authority must be at most ${MAX_CA_LENGTH} characters.`)
        return
      }
    }

    try {
      await onSave(trimmedEndpoint, caToSend)
    } catch (err) {
      setError(extractValidationMessage(err) ?? getErrorMessage(err) ?? 'Failed to save the Qubiz Titan settings. Please try again.')
    }
  }

  const handleDelete = async () => {
    try {
      await onDelete()
    } catch (err) {
      setError(extractValidationMessage(err) ?? getErrorMessage(err) ?? 'Failed to remove the Qubiz Titan settings. Please try again.')
    }
  }

  return (
    <section aria-label={title} data-testid={`${testIdPrefix}-section`}>
      <h2 className="text-xl font-bold mb-2 my-8">{title}</h2>
      <hr className="my-2" />
      <p className="mb-6 text-sm text-muted-foreground">{description}</p>

      <div
        className="mb-4 flex items-start gap-2 rounded border border-info-foreground bg-info px-3 py-2 text-sm text-info-foreground"
        data-testid={`${testIdPrefix}-personal-key-note`}
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Titan keys are personal, so there is no tenant-wide key here. Each user connects their own key on
          the project's Connectors page, and Qubiz Titan becomes available to them once both this endpoint and their
          key are set.
        </span>
      </div>

      {isLoading ? (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          Loading...
        </p>
      ) : loadError ? (
        <SettingsLoadError
          error={loadError}
          fallbackMessage="Could not load the Qubiz Titan settings. The configured endpoint, if any, is unchanged."
          onRetry={onRetryLoad}
          testId={`${testIdPrefix}-load-error`}
        />
      ) : (
        <>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={endpointInputId}>Endpoint</FieldLabel>
              <Input
                id={endpointInputId}
                type="text"
                placeholder="https://titan.example.com/anthropic"
                value={endpointValue}
                onChange={e => {
                  setEndpointValue(e.target.value)
                  setError(null)
                }}
                data-testid={endpointInputId}
              />
              <p className="text-sm text-muted-foreground">
                The Anthropic-compatible address of your Qubiz Titan instance, set once for the whole tenant.
                The model is platform-set — users cannot pick a model for Qubiz Titan.
              </p>
            </Field>

            <Field>
              <FieldLabel htmlFor={caInputId}>Certificate authority (optional)</FieldLabel>
              <Textarea
                id={caInputId}
                rows={6}
                className="font-mono text-xs"
                placeholder="-----BEGIN CERTIFICATE-----"
                value={caValue}
                onChange={e => {
                  setCaValue(e.target.value)
                  setCaTouched(true)
                  setError(null)
                }}
                data-testid={caInputId}
              />
              <p className="text-sm text-muted-foreground">
                Needed only when the endpoint serves a certificate issued by an internal authority, in PEM
                format. Paste the authority's certificate, not a private key. Leave empty when the endpoint
                uses a publicly trusted certificate.
              </p>
            </Field>

            {error && (
              <p role="alert" className="text-sm text-destructive" data-testid={`${testIdPrefix}-error`}>
                {error}
              </p>
            )}
          </FieldGroup>

          <div className="mt-6 flex justify-end gap-3">
            {isConfigured && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={isDeleting}
                data-testid={`${testIdPrefix}-delete-button`}
              >
                {isDeleting ? 'Removing...' : 'Remove Qubiz Titan'}
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={isSaving}
              data-testid={`${testIdPrefix}-save-button`}
            >
              <Check />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </>
      )}
    </section>
  )
}
